/**
 * Hora civil digitada -> instante UTC, no fuso da AULA e nao no do navegador.
 *
 * `<input type="datetime-local">` devolve 'AAAA-MM-DDTHH:mm' sem fuso nenhum, e
 * `new Date('2026-08-20T14:00')` resolve essa hora civil pelo fuso do sistema
 * operacional de quem digitou. A plataforma inteira EXIBE no fuso da pessoa
 * (`i18n/datetime.ts` existe para isso): o admin que resolvia de Europe/Lisbon
 * uma colisao ancorada em America/Sao_Paulo digitava 14:00, gravava um instante
 * que e 10:00 em Sao_Paulo, e a mesma tela re-renderizada mostrava um horario
 * diferente do que ele acabara de escrever. A aula andava quatro horas sem que
 * ninguem tivesse pedido.
 *
 * `i18n/datetime.ts` so sabe o caminho de ida (instante -> hora civil). Esta e a
 * volta, e ela mora aqui — junto das duas telas que digitam horario de aula —
 * ate que o catalogo de datas ganhe dono para recebe-la.
 *
 * O calculo e a inversa de `partsInPersonTimezone`: um candidato, o erro que ele
 * comete quando lido naquele fuso, e a correcao. **Duas passadas, e nao uma**:
 * na virada do horario de verao o offset do candidato e o do resultado diferem,
 * e a primeira correcao pode cair do outro lado da virada. A alternativa
 * recusada foi uma tabela de offsets — ela envelhece a cada mudanca de regra de
 * fuso, enquanto `Intl` acompanha os dados do runtime.
 */

const LOCAL_INPUT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::\d{2})?$/;

const FORMATTERS = new Map<string, Intl.DateTimeFormat>();

/**
 * `hourCycle: 'h23'` e escrito, e nao herdado do locale: com `hour12: false` o
 * ICU devolve "24" para a meia-noite em varios locales, e "24" viraria o dia
 * seguinte na remontagem abaixo. O locale e fixo porque nada aqui e exibido —
 * sao numeros para calcular, e formatar no idioma de quem olha seria comparar
 * tambem o idioma.
 */
function partsFormatter(timezone: string): Intl.DateTimeFormat {
  const cached = FORMATTERS.get(timezone);
  if (cached !== undefined) return cached;

  const made = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  });
  FORMATTERS.set(timezone, made);
  return made;
}

/**
 * A hora civil do instante naquele fuso, remontada como se fosse UTC. O valor
 * nao e um instante: e so a forma de subtrair duas horas civis sem passar por
 * texto formatado.
 */
function wallClockMs(instantMs: number, timezone: string): number {
  const parts = new Map(
    partsFormatter(timezone)
      .formatToParts(new Date(instantMs))
      .map((part) => [part.type, part.value]),
  );

  return Date.UTC(
    Number(parts.get('year')),
    Number(parts.get('month')) - 1,
    Number(parts.get('day')),
    Number(parts.get('hour')),
    Number(parts.get('minute')),
    Number(parts.get('second')),
  );
}

/** `true` quando o identificador e um fuso IANA que este runtime conhece. */
export function isKnownTimezone(timezone: string): boolean {
  if (timezone.trim().length === 0) return false;
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/**
 * Instante UTC de uma hora civil ('AAAA-MM-DDTHH:mm') lida naquele fuso.
 *
 * Hora inexistente (a que a virada de primavera pula) e hora ambigua (a que a
 * virada de outono repete) nao lancam: a iteracao assenta no instante vizinho,
 * porque recusar o envio de um horario que o proprio navegador ofereceu no
 * seletor seria trocar um erro de uma hora por um formulario que nao submete.
 */
export function instantFromWallClock(local: string, timezone: string): string {
  const match = LOCAL_INPUT.exec(local);
  if (match === null) throw new TypeError(`hora civil invalida: ${local}`);

  const target = Date.UTC(
    Number(match[1]),
    Number(match[2]) - 1,
    Number(match[3]),
    Number(match[4]),
    Number(match[5]),
  );

  let instant = target;
  for (let pass = 0; pass < 2; pass += 1) {
    instant = target - (wallClockMs(instant, timezone) - instant);
  }
  return new Date(instant).toISOString();
}

/**
 * Meia-noite do dia corrente NAQUELE fuso, em UTC.
 *
 * A agenda pedia `from = agora`, e "agora" tem milissegundo: a chave da consulta
 * nascia inedita a cada montagem e o cache nunca servia duas visitas seguidas.
 * Pior que o cache: as 14h a aba "Dia" ja nao mostrava a aula das 8h — para o
 * aluno a aula sumia, e a aula EM ANDAMENTO (que comecou minutos atras) sumia
 * junto, levando com ela o unico caminho ate a sala.
 */
export function startOfLocalDay(now: Date, timezone: string): string {
  const parts = new Map(
    partsFormatter(timezone)
      .formatToParts(now)
      .map((part) => [part.type, part.value]),
  );
  const day = `${parts.get('year')}-${parts.get('month')}-${parts.get('day')}T00:00`;
  return instantFromWallClock(day, timezone);
}
